import { Connection, PublicKey } from '@solana/web3.js';

import type { BurnableAsset } from './dasApi';

// Estimates use the live balance of the accounts a burn closes (BurnableAsset.lamports).
// Solana lowers the rent-exempt minimum in steps (SIMD-0437) and accounts created
// before a step keep their old deposit, so no per-type constant fits every account.

export interface ReclaimEstimate {
  assetMint: string;
  assetName: string;
  estimatedLamports: number;
  breakdown: {
    tokenAccount?: number;
    metadataAccount?: number;
    assetAccount?: number;
  };
}

export interface TotalReclaimEstimate {
  totalGrossLamports: number; // Total before fees
  totalNetLamports: number; // After platform fees
  platformFeeLamports: number;
  feePercentage: number;
  assets: ReclaimEstimate[];
}

/**
 * Estimate reclaimable lamports for a single asset
 */
export function estimateAssetRent(asset: BurnableAsset): ReclaimEstimate {
  const estimatedLamports = asset.lamports ?? 0;
  const breakdown: ReclaimEstimate['breakdown'] = {};

  switch (asset.type) {
    case 'spl_token':
    case 'token_2022':
      breakdown.tokenAccount = estimatedLamports;
      break;

    case 'metadata_nft':
      breakdown.metadataAccount = estimatedLamports;
      break;

    case 'mpl_core_nft':
      breakdown.assetAccount = estimatedLamports;
      break;
  }

  return {
    assetMint: asset.mint,
    assetName: asset.name,
    estimatedLamports,
    breakdown,
  };
}

/**
 * Get actual rent-exempt amount for an account
 * More accurate than estimates, but requires RPC call
 */
export async function getActualAccountRent(
  connection: Connection,
  accountAddress: PublicKey
): Promise<number> {
  try {
    const accountInfo = await connection.getAccountInfo(accountAddress);
    return accountInfo?.lamports || 0;
  } catch (error) {
    console.warn(`Failed to fetch account rent for ${accountAddress.toString()}:`, error);
    return 0;
  }
}

/**
 * Calculate total reclaimable lamports for multiple assets
 * Applies platform fee deduction
 */
export function calculateTotalReclaim(
  assets: BurnableAsset[],
  tokenFeesBps: number = 230, // GlobalConfig.tokenFeesBps on mainnet; callers pass the live value
  nftFeesBps: number = 500 // GlobalConfig.nftFeesBps on mainnet
): TotalReclaimEstimate {
  console.log('🔍 [calculateTotalReclaim] Called with', assets.length, 'assets');
  console.log('🔍 [calculateTotalReclaim] Fee BPS: token=', tokenFeesBps, 'nft=', nftFeesBps);

  const assetEstimates: ReclaimEstimate[] = [];
  let totalGrossLamports = 0;

  for (const asset of assets) {
    const estimate = estimateAssetRent(asset);
    console.log('🔍 [calculateTotalReclaim] Asset:', {
      mint: asset.mint.slice(0, 8),
      type: asset.type,
      isNFT: asset.isNFT,
      estimatedLamports: estimate.estimatedLamports,
    });
    assetEstimates.push(estimate);
    totalGrossLamports += estimate.estimatedLamports;
  }

  console.log('🔍 [calculateTotalReclaim] Total gross lamports:', totalGrossLamports);

  // Calculate weighted average fee based on asset types
  let totalFeeWeightedLamports = 0;

  for (let i = 0; i < assets.length; i++) {
    const asset = assets[i];
    const estimate = assetEstimates[i];
    const feesBps = asset.isNFT ? nftFeesBps : tokenFeesBps;

    const assetFee = Math.floor((estimate.estimatedLamports * feesBps) / 10000);
    console.log('🔍 [calculateTotalReclaim] Asset fee calculation:', {
      mint: asset.mint.slice(0, 8),
      isNFT: asset.isNFT,
      estimatedLamports: estimate.estimatedLamports,
      feesBps,
      assetFee,
    });
    totalFeeWeightedLamports += assetFee;
  }

  const platformFeeLamports = totalFeeWeightedLamports;
  const totalNetLamports = totalGrossLamports - platformFeeLamports;
  const feePercentage = totalGrossLamports > 0 ? (platformFeeLamports / totalGrossLamports) * 100 : 0;

  console.log('🔍 [calculateTotalReclaim] Final result:', {
    totalGrossLamports,
    platformFeeLamports,
    totalNetLamports,
    feePercentage,
  });

  return {
    totalGrossLamports,
    totalNetLamports,
    platformFeeLamports,
    feePercentage,
    assets: assetEstimates,
  };
}

/**
 * Format lamports to SOL with proper decimals
 */
export function lamportsToSol(lamports: number): string {
  return (lamports / 1e9).toFixed(6);
}

/**
 * Sum the live rent deposit of token accounts, in SOL. Used by the swap flows,
 * where closing the token account returns its deposit to the user.
 */
export function sumTokenAccountRentSol(tokens: Array<{ lamports?: number }>): number {
  return tokens.reduce((sum, t) => sum + (t.lamports ?? 0), 0) / 1e9;
}

/**
 * Get detailed breakdown for display
 */
export function formatReclaimEstimate(estimate: TotalReclaimEstimate): string {
  const lines = [
    `💰 Total Gross Rent: ${lamportsToSol(estimate.totalGrossLamports)} SOL`,
    `💸 Platform Fee (${estimate.feePercentage.toFixed(2)}%): ${lamportsToSol(estimate.platformFeeLamports)} SOL`,
    `✅ Net Refund: ${lamportsToSol(estimate.totalNetLamports)} SOL`,
    ``,
    `📦 Assets (${estimate.assets.length}):`,
  ];

  for (const asset of estimate.assets) {
    lines.push(`  • ${asset.assetName}: ${lamportsToSol(asset.estimatedLamports)} SOL`);
  }

  return lines.join('\n');
}

// Minimum SOL required for various operations (in lamports)
// These are estimates based on transaction fees and rent requirements
export const MIN_SOL_REQUIREMENTS = {
  // Base transaction fee (per signature, ~5000 lamports = 0.000005 SOL)
  TRANSACTION_FEE: 5000,

  // Minimum for burn operations (need SOL for transaction fees only)
  // Most rent is reclaimed from burned accounts
  BURN: 10000, // 0.00001 SOL

  // Minimum for open chest operations
  // Transaction fee + potential rent for new accounts
  OPEN_CHEST: 10000, // 0.00001 SOL (rent is covered by program)

  // Minimum for deposit operations
  // Need to cover transaction fee + any rent for new accounts
  DEPOSIT: 15000, // 0.000015 SOL

  // Minimum for withdraw operations
  WITHDRAW: 10000, // 0.00001 SOL
};

export interface BalanceCheckResult {
  hasEnough: boolean;
  currentBalance: number; // in lamports
  requiredBalance: number; // in lamports
  shortfall: number; // in lamports (0 if has enough)
  message: string;
}

/**
 * Check if a wallet has sufficient SOL balance for an operation
 * @param connection - Solana connection
 * @param walletAddress - Wallet public key
 * @param requiredLamports - Minimum lamports required
 * @param operationType - Type of operation for error messaging
 */
export async function checkSufficientBalance(
  connection: Connection,
  walletAddress: PublicKey,
  requiredLamports: number = MIN_SOL_REQUIREMENTS.TRANSACTION_FEE,
  operationType: 'burn' | 'open_chest' | 'deposit' | 'withdraw' = 'burn'
): Promise<BalanceCheckResult> {
  try {
    const balance = await connection.getBalance(walletAddress);

    const hasEnough = balance >= requiredLamports;
    const shortfall = hasEnough ? 0 : requiredLamports - balance;

    let message = '';
    if (!hasEnough) {
      const balanceSOL = (balance / 1e9).toFixed(6);
      const requiredSOL = (requiredLamports / 1e9).toFixed(6);
      const shortfallSOL = (shortfall / 1e9).toFixed(6);

      const operationName = {
        burn: 'burn tokens',
        open_chest: 'open chests',
        deposit: 'deposit',
        withdraw: 'withdraw',
      }[operationType];

      message = `Insufficient SOL to ${operationName}. You have ${balanceSOL} SOL but need at least ${requiredSOL} SOL. Please add ${shortfallSOL} SOL to your wallet.`;
    }

    return {
      hasEnough,
      currentBalance: balance,
      requiredBalance: requiredLamports,
      shortfall,
      message,
    };
  } catch (error) {
    console.error('Failed to check balance:', error);
    // If we can't check balance, assume it's okay to proceed
    // The transaction will fail with a more specific error if there's an issue
    return {
      hasEnough: true,
      currentBalance: 0,
      requiredBalance: requiredLamports,
      shortfall: 0,
      message: '',
    };
  }
}

/**
 * Get minimum required SOL for an operation type
 */
export function getMinimumRequiredSol(operationType: keyof typeof MIN_SOL_REQUIREMENTS): number {
  return MIN_SOL_REQUIREMENTS[operationType];
}
